import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
    Search,
    SlidersHorizontal,
    Wifi,
    Car,
    Building,
    Droplets,
    Shield,
    PawPrint,
    Zap,
    Loader,
    ChevronDown,
    ChevronUp,
    X,
    MapPin,
    Home,
    Bed,
    Bath,
    DollarSign,
    Calendar,
    Star,
    Filter,
    ArrowUpDown
} from 'lucide-react';
import {API_CONSTANTS} from "../../Constants";

// API Service for Property Filters
class PropertyFiltersAPI {
    static async getFilters() {
        try {
            const response = await fetch(`${API_CONSTANTS.BASE_URL}/search/filters`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(localStorage.getItem('authToken') && {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    })
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching filter options:', error);
            throw error;
        }
    }

    static async getPropertyListings(filters = {}) {
        try {
            const queryParams = new URLSearchParams();

            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        if (value.length > 0) {
                            queryParams.append(key, value.join(','));
                        }
                    } else if (typeof value === 'object' && value.min !== undefined && value.max !== undefined) {
                        if (value.min) queryParams.append('minPrice', value.min);
                        if (value.max) queryParams.append('maxPrice', value.max);
                    } else {
                        queryParams.append(key, value.toString());
                    }
                }
            });

            const response = await fetch(`${API_CONSTANTS.BASE_URL}/properties/listings?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(localStorage.getItem('authToken') && {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    })
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching property listings:', error);
            throw error;
        }
    }
}

// Custom Range Slider Component
// Two overlapping native <input type="range"> elements don't reliably
// stack/paint as independent thumbs across browsers - in practice only one
// ever ends up draggable. This drives two custom thumbs off pointer events
// on a single shared track instead, which doesn't have that problem.
const RangeSlider = ({min, max, value, onChange, formatValue, step = 1000}) => {
    const trackRef = useRef(null);
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const minVal = (localValue.min === '' || localValue.min == null) ? min : Number(localValue.min);
    const maxVal = (localValue.max === '' || localValue.max == null) ? max : Number(localValue.max);

    const clamp = (v) => Math.max(min, Math.min(max, v));
    const snapToStep = (v) => clamp(Math.round((v - min) / step) * step + min);

    const valueFromClientX = (clientX) => {
        const rect = trackRef.current.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        return snapToStep(min + pct * (max - min));
    };

    const updateValue = (type, rawValue) => {
        setLocalValue((prev) => {
            const currentMin = (prev.min === '' || prev.min == null) ? min : Number(prev.min);
            const currentMax = (prev.max === '' || prev.max == null) ? max : Number(prev.max);
            const updated = type === 'min'
                ? {min: Math.min(rawValue, currentMax), max: currentMax}
                : {min: currentMin, max: Math.max(rawValue, currentMin)};
            onChange(updated);
            return updated;
        });
    };

    const startDrag = (type) => (e) => {
        e.preventDefault();
        const handleMove = (moveEvent) => {
            const clientX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
            updateValue(type, valueFromClientX(clientX));
        };
        const handleUp = () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleUp);
        };
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        window.addEventListener('touchmove', handleMove, {passive: false});
        window.addEventListener('touchend', handleUp);
    };

    const handleKeyDown = (type) => (e) => {
        const current = type === 'min' ? minVal : maxVal;
        const delta = e.shiftKey ? step * 10 : step;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            updateValue(type, clamp(current + delta));
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            updateValue(type, clamp(current - delta));
        } else if (e.key === 'Home') {
            e.preventDefault();
            updateValue(type, min);
        } else if (e.key === 'End') {
            e.preventDefault();
            updateValue(type, max);
        }
    };

    const span = max - min;
    const percentage = {
        min: span > 0 ? ((minVal - min) / span) * 100 : 0,
        max: span > 0 ? ((maxVal - min) / span) * 100 : 100
    };

    const thumbClass = "absolute top-1/2 w-5 h-5 bg-white border-2 border-highland-700 rounded-full shadow-md cursor-pointer -translate-x-1/2 -translate-y-1/2 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-highland-500/30 transition-transform touch-none";

    return (
        <div className="space-y-4">
            <div ref={trackRef} className="relative h-2 bg-soil-100 rounded-full select-none touch-none">
                <div
                    className="absolute h-2 bg-highland-500 rounded-full"
                    style={{
                        left: `${percentage.min}%`,
                        width: `${percentage.max - percentage.min}%`
                    }}
                />
                <div
                    role="slider"
                    tabIndex={0}
                    aria-label="Minimum price"
                    aria-valuemin={min}
                    aria-valuemax={max}
                    aria-valuenow={minVal}
                    onMouseDown={startDrag('min')}
                    onTouchStart={startDrag('min')}
                    onKeyDown={handleKeyDown('min')}
                    className={thumbClass}
                    style={{left: `${percentage.min}%`}}
                />
                <div
                    role="slider"
                    tabIndex={0}
                    aria-label="Maximum price"
                    aria-valuemin={min}
                    aria-valuemax={max}
                    aria-valuenow={maxVal}
                    onMouseDown={startDrag('max')}
                    onTouchStart={startDrag('max')}
                    onKeyDown={handleKeyDown('max')}
                    className={thumbClass}
                    style={{left: `${percentage.max}%`}}
                />
            </div>
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4 text-soil-400"/>
                    <span className="text-sm font-medium text-soil-600">
                        {formatValue(minVal)}
                    </span>
                </div>
                <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-soil-600">
                        {formatValue(maxVal)}
                    </span>
                    <DollarSign className="w-4 h-4 text-soil-400"/>
                </div>
            </div>
        </div>
    );
};

// Enhanced SearchAndFilters component
export const EnhancedSearchAndFilters = ({
                                             searchQuery,
                                             setSearchQuery,
                                             sortBy,
                                             setSortBy,
                                             filters,
                                             setFilters,
                                             showFilters,
                                             setShowFilters,
                                             filteredProperties,
                                             properties,
                                             clearFilters,
                                             onFiltersChange
                                         }) => {
    const [filterOptions, setFilterOptions] = useState({
        locations: [],
        propertyTypes: [],
        bedroomOptions: [],
        bathroomOptions: [],
        amenities: [],
        availabilityOptions: [],
        sortOptions: [],
        priceRange: {min: 0, max: 500000}
    });
    const [loadingFilters, setLoadingFilters] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    // Local state for form inputs
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
    const [localFilters, setLocalFilters] = useState(filters);
    const [localSortBy, setLocalSortBy] = useState(sortBy);

    // Sync local state with props
    useEffect(() => {
        setLocalSearchQuery(searchQuery);
        setLocalFilters(filters);
        setLocalSortBy(sortBy);
    }, [searchQuery, filters, sortBy]);

    // Default amenity options with icons
    const defaultAmenityOptions = [
        {id: 'wifi', label: 'WiFi', icon: Wifi, color: 'blue'},
        {id: 'parking', label: 'Parking', icon: Car, color: 'green'},
        {id: 'gym', label: 'Gym', icon: Building, color: 'purple'},
        {id: 'pool', label: 'Pool', icon: Droplets, color: 'cyan'},
        {id: 'security', label: '24/7 Security', icon: Shield, color: 'red'},
        {id: 'elevator', label: 'Elevator', icon: Building, color: 'gray'},
        {id: 'garden', label: 'Garden', icon: Building, color: 'emerald'},
        {id: 'pets', label: 'Pet Friendly', icon: PawPrint, color: 'orange'},
        {id: 'water', label: 'Water', icon: Droplets, color: 'blue'},
        {id: 'electricity', label: 'Electricity', icon: Zap, color: 'yellow'}
    ];

    // Load filter options from API
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                setLoadingFilters(true);
                const response = await PropertyFiltersAPI.getFilters();

                if (response) {
                    const apiData = response;

                    // With very few live listings, min and max can come back
                    // identical (or missing) - that's a zero-width range with
                    // nothing to actually drag between, so fall back to a
                    // usable default span instead of a frozen-looking slider.
                    const rawPriceRange = apiData.priceRange;
                    const priceRange = (rawPriceRange && rawPriceRange.min !== rawPriceRange.max)
                        ? rawPriceRange
                        : {min: 0, max: 100000};

                    setFilterOptions({
                        locations: apiData.locations || [],
                        propertyTypes: apiData.propertyTypes || [],
                        bedroomOptions: apiData.bedroomOptions || [
                            {value: '1', label: '1+', count: 0},
                            {value: '2', label: '2+', count: 0},
                            {value: '3', label: '3+', count: 0},
                            {value: '4', label: '4+', count: 0}
                        ],
                        bathroomOptions: apiData.bathroomOptions || [
                            {value: '1', label: '1+', count: 0},
                            {value: '2', label: '2+', count: 0},
                            {value: '3', label: '3+', count: 0}
                        ],
                        amenities: apiData.amenities?.map(amenity => {
                            const defaultAmenity = defaultAmenityOptions.find(d =>
                                d.id === amenity.id || d.label.toLowerCase() === amenity.name.toLowerCase()
                            );
                            return {
                                id: amenity.id || amenity.name.toLowerCase().replace(/\s+/g, '-'),
                                label: amenity.name || amenity.label,
                                icon: defaultAmenity?.icon || Building,
                                color: defaultAmenity?.color || 'gray',
                                count: amenity.count || 0
                            };
                        }) || defaultAmenityOptions,
                        availabilityOptions: apiData.availabilityOptions || [
                            {value: 'all', label: 'All Properties'},
                            {value: 'immediate', label: 'Available Now'},
                            {value: 'within_month', label: 'Within 1 Month'}
                        ],
                        sortOptions: apiData.sortOptions || [
                            {value: 'newest', label: 'Newest First', icon: Calendar},
                            {value: 'price_low', label: 'Price: Low to High', icon: ArrowUpDown},
                            {value: 'price_high', label: 'Price: High to Low', icon: ArrowUpDown},
                            {value: 'rating', label: 'Highest Rated', icon: Star},
                            {value: 'bedrooms', label: 'Most Bedrooms', icon: Bed}
                        ],
                        priceRange
                    });
                }
            } catch (error) {
                console.error('Failed to load filter options:', error);
            } finally {
                setLoadingFilters(false);
            }
        };

        loadFilterOptions();
    }, []);

    // Execute search
    const executeSearch = useCallback(async () => {
        try {
            setIsSearching(true);
            setSearchQuery(localSearchQuery);
            setFilters(localFilters);
            setSortBy(localSortBy);

            const sortMapping = {
                'newest': {sortBy: 'date_created', sortOrder: 'desc'},
                'price_low': {sortBy: 'price', sortOrder: 'asc'},
                'price_high': {sortBy: 'price', sortOrder: 'desc'},
                'rating': {sortBy: 'rating', sortOrder: 'desc'},
                'bedrooms': {sortBy: 'bedrooms', sortOrder: 'desc'}
            };

            const sortConfig = sortMapping[localSortBy] || {sortBy: 'date_created', sortOrder: 'desc'};

            if (onFiltersChange) {
                await onFiltersChange({
                    ...localFilters,
                    search: localSearchQuery,
                    ...sortConfig
                });
            }
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsSearching(false);
        }
    }, [localSearchQuery, localFilters, localSortBy, setSearchQuery, setFilters, setSortBy, onFiltersChange]);

    // Handle local filter changes
    const handleLocalFilterChange = useCallback((filterType, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    }, []);

    // Handle local amenity toggle
    const handleLocalAmenityToggle = useCallback((amenityId) => {
        setLocalFilters(prev => {
            const newAmenities = prev.amenities.includes(amenityId)
                ? prev.amenities.filter(id => id !== amenityId)
                : [...prev.amenities, amenityId];

            return {
                ...prev,
                amenities: newAmenities
            };
        });
    }, []);

    // Clear all filters
    const handleClearFilters = useCallback(() => {
        const clearedFilters = {
            location: '',
            propertyType: '',
            bedrooms: '',
            bathrooms: '',
            priceRange: {min: '', max: ''},
            availability: 'all',
            amenities: []
        };

        setLocalFilters(clearedFilters);
        setLocalSearchQuery('');
        setLocalSortBy('newest');
        setFilters(clearedFilters);
        setSearchQuery('');
        setSortBy('newest');
        clearFilters();

        if (onFiltersChange) {
            onFiltersChange({
                ...clearedFilters,
                search: '',
                sortBy: 'date_created',
                sortOrder: 'desc'
            });
        }
    }, [setFilters, setSearchQuery, setSortBy, clearFilters, onFiltersChange]);

    // Check active filters
    const hasActiveFilters = Object.values(localFilters).some(v =>
        v && (Array.isArray(v) ? v.length > 0 : v !== '' && v !== 'all')
    ) || localSearchQuery.trim() !== '';

    // Handle Enter key press
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            executeSearch();
        }
    };

    // Format price
    const formatPrice = (price) => {
        return `KES ${parseInt(price).toLocaleString()}`;
    };

    return (
        <div
            className="bg-white rounded-2xl shadow-xl mb-8 overflow-hidden transition-all duration-300 border border-soil-100">
            {/* Header Section */}
            <div className="bg-karibu-100 p-6 border-b border-soil-100">
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex-1 relative group min-w-[200px]">
                        <Search
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-soil-400 w-5 h-5 group-focus-within:text-highland-500 transition-colors duration-200"/>
                        <input
                            type="text"
                            value={localSearchQuery}
                            onChange={(e) => setLocalSearchQuery(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            placeholder="Search properties, locations, or keywords..."
                            className="w-full pl-12 pr-4 py-4 bg-white border-2 border-soil-100 rounded-xl focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 text-soil-600 placeholder-soil-400 shadow-sm"
                            aria-label="Search properties"
                        />
                    </div>

                    <button
                        onClick={executeSearch}
                        disabled={isSearching}
                        className="px-8 py-4 bg-highland-500 hover:bg-highland-600 disabled:bg-soil-400 text-white font-semibold rounded-xl transition-colors duration-200 flex items-center space-x-3 min-w-[140px] justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        aria-label="Execute search"
                    >
                        {isSearching ? (
                            <>
                                <Loader className="w-5 h-5 animate-spin"/>
                                <span>Searching...</span>
                            </>
                        ) : (
                            <>
                                <Search className="w-5 h-5"/>
                                <span>Search</span>
                            </>
                        )}
                    </button>

                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="px-6 py-4 bg-white border-2 border-soil-100 hover:border-highland-500 text-soil-600 hover:text-highland-500 rounded-xl transition-all duration-200 flex items-center space-x-3 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 relative"
                        aria-label="Toggle filters"
                    >
                        <Filter className="w-5 h-5"/>
                        <span className="hidden sm:inline font-medium">Filters</span>
                        {hasActiveFilters && (
                            <div
                                className="absolute -top-2 -right-2 w-6 h-6 bg-murram-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
                                {Object.values(localFilters).filter(v =>
                                    v && (Array.isArray(v) ? v.length > 0 : v !== '' && v !== 'all')
                                ).length + (localSearchQuery ? 1 : 0)}
                            </div>
                        )}
                        {isExpanded ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
                    </button>

                    {hasActiveFilters && (
                        <button
                            onClick={handleClearFilters}
                            className="px-4 py-4 text-soil-400 hover:text-murram-600 hover:bg-murram-100/40 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                            title="Clear all filters"
                            aria-label="Clear filters"
                        >
                            <X className="w-5 h-5"/>
                        </button>
                    )}
                </div>

                {/* Active Filters Pills */}
                {hasActiveFilters && !isExpanded && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {localSearchQuery && (
                            <span
                                className="inline-flex items-center px-3 py-2 bg-highland-50 text-highland-600 text-sm font-medium rounded-full border border-highland-100 shadow-sm">
                                <Search className="w-3 h-3 mr-1"/>
                                "{localSearchQuery}"
                            </span>
                        )}
                        {localFilters.location && (
                            <span
                                className="inline-flex items-center px-3 py-2 bg-highland-50 text-highland-600 text-sm font-medium rounded-full border border-highland-100 shadow-sm">
                                <MapPin className="w-3 h-3 mr-1"/>
                                {filterOptions.locations.find(l => l.slug === localFilters.location)?.name || localFilters.location}
                            </span>
                        )}
                        {localFilters.propertyType && (
                            <span
                                className="inline-flex items-center px-3 py-2 bg-jacaranda-100 text-jacaranda-600 text-sm font-medium rounded-full border border-jacaranda-100 shadow-sm">
                                <Home className="w-3 h-3 mr-1"/>
                                {filterOptions.propertyTypes.find(t => t.name === localFilters.propertyType)?.name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || localFilters.propertyType}
                            </span>
                        )}
                        {localFilters.bedrooms && (
                            <span
                                className="inline-flex items-center px-3 py-2 bg-murram-100 text-murram-600 text-sm font-medium rounded-full border border-murram-100 shadow-sm">
                                <Bed className="w-3 h-3 mr-1"/>
                                {localFilters.bedrooms}+ beds
                            </span>
                        )}
                        {localFilters.bathrooms && (
                            <span
                                className="inline-flex items-center px-3 py-2 bg-murram-100 text-murram-600 text-sm font-medium rounded-full border border-murram-100 shadow-sm">
                                <Bath className="w-3 h-3 mr-1"/>
                                {localFilters.bathrooms}+ baths
                            </span>
                        )}
                        {localFilters.amenities.length > 0 && (
                            <span
                                className="inline-flex items-center px-3 py-2 bg-jacaranda-100 text-jacaranda-600 text-sm font-medium rounded-full border border-jacaranda-100 shadow-sm">
                                <Star className="w-3 h-3 mr-1"/>
                                {localFilters.amenities.length} amenities
                            </span>
                        )}
                        {(localFilters.priceRange.min || localFilters.priceRange.max) && (
                            <span
                                className="inline-flex items-center px-3 py-2 bg-dawn-100 text-dawn-600 text-sm font-medium rounded-full border border-dawn-100 shadow-sm">
                                <DollarSign className="w-3 h-3 mr-1"/>
                                {localFilters.priceRange.min ? formatPrice(localFilters.priceRange.min) : '0'} - {localFilters.priceRange.max ? formatPrice(localFilters.priceRange.max) : '∞'}
                            </span>
                        )}
                        {localFilters.availability !== 'all' && (
                            <span
                                className="inline-flex items-center px-3 py-2 bg-highland-50 text-highland-600 text-sm font-medium rounded-full border border-highland-100 shadow-sm">
                                <Calendar className="w-3 h-3 mr-1"/>
                                {filterOptions.availabilityOptions.find(a => a.value === localFilters.availability)?.label || localFilters.availability}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Expanded Filters Section */}
            {isExpanded && (
                <div className="p-8 bg-soil-50">
                    {loadingFilters && (
                        <div className="flex items-center justify-center py-8 mb-6">
                            <div className="bg-white rounded-xl p-6 shadow-lg flex items-center space-x-3">
                                <Loader className="w-6 h-6 animate-spin text-highland-500"/>
                                <span className="text-soil-600 font-medium">Loading filter options...</span>
                            </div>
                        </div>
                    )}

                    {/* Sort Section */}
                    <div className="mb-8">
                        <div className="flex items-center space-x-2 mb-4">
                            <ArrowUpDown className="w-5 h-5 text-soil-600"/>
                            <label className="font-display text-lg font-semibold text-soil-900">Sort By</label>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {filterOptions.sortOptions.map(option => {
                                const Icon = option.icon || Calendar;
                                return (
                                    <button
                                        key={option.value}
                                        onClick={() => setLocalSortBy(option.value)}
                                        className={`flex items-center space-x-2 px-4 py-3 rounded-xl border-2 transition-all duration-200 font-medium text-sm ${
                                            localSortBy === option.value
                                                ? 'border-highland-500 bg-highland-50 text-highland-600 shadow-md'
                                                : 'border-soil-100 bg-white text-soil-600 hover:border-highland-400 hover:bg-highland-50 shadow-sm hover:shadow-md'
                                        }`}
                                        aria-label={`Sort by ${option.label}`}
                                    >
                                        <Icon className="w-4 h-4"/>
                                        <span>{option.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Main Filters Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        {/* Left Column */}
                        <div className="space-y-6">
                            {/* Location Filter */}
                            <div className="bg-white rounded-xl p-6 shadow-lg border border-soil-100">
                                <div className="flex items-center space-x-2 mb-4">
                                    <MapPin className="w-5 h-5 text-highland-500"/>
                                    <label className="font-display text-lg font-semibold text-soil-900">Location</label>
                                </div>
                                <select
                                    value={localFilters.location}
                                    onChange={(e) => handleLocalFilterChange('location', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                    aria-label="Select location"
                                >
                                    <option value="">All Locations</option>
                                    {filterOptions.locations.map(location => (
                                        <option key={location.neighborhood_id} value={location.slug}>
                                            {location.name} ({location.city}) {location.count > 0 && `(${location.count})`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Property Type Filter */}
                            <div className="bg-white rounded-xl p-6 shadow-lg border border-soil-100">
                                <div className="flex items-center space-x-2 mb-4">
                                    <Home className="w-5 h-5 text-jacaranda-500"/>
                                    <label className="font-display text-lg font-semibold text-soil-900">Property Type</label>
                                </div>
                                <select
                                    value={localFilters.propertyType}
                                    onChange={(e) => handleLocalFilterChange('propertyType', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-jacaranda-100 focus:border-jacaranda-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                    aria-label="Select property type"
                                >
                                    <option value="">All Types</option>
                                    {filterOptions.propertyTypes.map(type => (
                                        <option key={type.type_id} value={type.name}>
                                            {type.name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} {type.count > 0 && `(${type.count})`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Bedrooms & Bathrooms */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white rounded-xl p-6 shadow-lg border border-soil-100">
                                    <div className="flex items-center space-x-2 mb-4">
                                        <Bed className="w-5 h-5 text-murram-500"/>
                                        <label className="font-display text-lg font-semibold text-soil-900">Bedrooms</label>
                                    </div>
                                    <select
                                        value={localFilters.bedrooms}
                                        onChange={(e) => handleLocalFilterChange('bedrooms', e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-murram-100 focus:border-murram-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                        aria-label="Select number of bedrooms"
                                    >
                                        <option value="">Any</option>
                                        {filterOptions.bedroomOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label} {option.count > 0 && `(${option.count})`}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="bg-white rounded-xl p-6 shadow-lg border border-soil-100">
                                    <div className="flex items-center space-x-2 mb-4">
                                        <Bath className="w-5 h-5 text-murram-500"/>
                                        <label className="font-display text-lg font-semibold text-soil-900">Bathrooms</label>
                                    </div>
                                    <select
                                        value={localFilters.bathrooms}
                                        onChange={(e) => handleLocalFilterChange('bathrooms', e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-murram-100 focus:border-murram-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                        aria-label="Select number of bathrooms"
                                    >
                                        <option value="">Any</option>
                                        {filterOptions.bathroomOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label} {option.count > 0 && `(${option.count})`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-6">
                            {/* Price Range Slider */}
                            <div className="bg-white rounded-xl p-6 shadow-lg border border-soil-100">
                                <div className="flex items-center space-x-2 mb-6">
                                    <DollarSign className="w-5 h-5 text-dawn-600"/>
                                    <label className="font-display text-lg font-semibold text-soil-900">Price Range</label>
                                </div>
                                <RangeSlider
                                    min={filterOptions.priceRange.min}
                                    max={filterOptions.priceRange.max}
                                    value={localFilters.priceRange}
                                    onChange={(value) => handleLocalFilterChange('priceRange', value)}
                                    formatValue={formatPrice}
                                    step={5000}
                                />
                            </div>

                            {/* Availability Filter */}
                            <div className="bg-white rounded-xl p-6 shadow-lg border border-soil-100">
                                <div className="flex items-center space-x-2 mb-4">
                                    <Calendar className="w-5 h-5 text-highland-500"/>
                                    <label className="font-display text-lg font-semibold text-soil-900">Availability</label>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {filterOptions.availabilityOptions.map(option => (
                                        <button
                                            key={option.value}
                                            onClick={() => handleLocalFilterChange('availability', option.value)}
                                            className={`px-4 py-3 rounded-lg border-2 transition-all duration-200 font-medium text-sm ${
                                                localFilters.availability === option.value
                                                    ? 'border-highland-500 bg-highland-50 text-highland-600 shadow-md'
                                                    : 'border-soil-100 bg-white text-soil-600 hover:border-highland-400 hover:bg-highland-50 shadow-sm hover:shadow-md'
                                            }`}
                                            aria-label={`Select availability ${option.label}`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Amenities Filter */}
                    <div className="bg-white rounded-xl p-6 shadow-lg border border-soil-100 mb-8">
                        <div className="flex items-center space-x-2 mb-4">
                            <Star className="w-5 h-5 text-jacaranda-500"/>
                            <label className="font-display text-lg font-semibold text-soil-900">Amenities</label>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {filterOptions.amenities.map(amenity => {
                                const Icon = amenity.icon || Building;
                                const isSelected = localFilters.amenities.includes(amenity.id);
                                return (
                                    <button
                                        key={amenity.id}
                                        onClick={() => handleLocalAmenityToggle(amenity.id)}
                                        className={`flex items-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all duration-200 text-sm ${
                                            isSelected
                                                ? 'border-highland-500 bg-highland-50 text-highland-600 shadow-md'
                                                : 'border-soil-100 bg-white text-soil-600 hover:border-highland-400 hover:bg-highland-50 shadow-sm hover:shadow-md'
                                        }`}
                                        aria-label={`Toggle amenity ${amenity.label}`}
                                    >
                                        <Icon className="w-4 h-4"/>
                                        <span>{amenity.label} {amenity.count > 0 && `(${amenity.count})`}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4">
                        <button
                            onClick={handleClearFilters}
                            className="px-6 py-3 bg-white border-2 border-soil-100 text-soil-600 hover:text-murram-600 hover:border-murram-500 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                            aria-label="Clear all filters"
                        >
                            Clear Filters
                        </button>
                        <button
                            onClick={executeSearch}
                            disabled={isSearching}
                            className="px-8 py-3 bg-highland-500 hover:bg-highland-600 disabled:bg-soil-400 text-white font-semibold rounded-xl transition-colors duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                            aria-label="Apply filters and search"
                        >
                            {isSearching ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin"/>
                                    <span>Applying...</span>
                                </>
                            ) : (
                                <>
                                    <Search className="w-5 h-5"/>
                                    <span>Apply Filters</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};